/**
 * `applySnapshot` — the reducer that turns the filtered snapshot stream into a fog-limited
 * `PlayerView`. Used by the client store and by bots (§4: same reducer, no fog cheats).
 * Pure data, no DOM.
 *
 *   const view = createPlayerView(viewInitFromWelcome(welcome, world.landCount));
 *   ws.onmessage = (m) => applySnapshot(view, decodeSnapshot(m.data));
 */
import type { EntityId, Ghost, MatchResult, MissileTrack, PlayerId, PlayerInfo, PlayerView, TeamId, MatchMode } from '@sa/sim';
import { BALANCE, SNAP_FLAG_CAPITALS_EXPOSED, SNAP_FLAG_ELIMINATED, SNAP_FLAG_FINAL, SNAP_FLAG_RELEASED } from '@sa/sim';
import type { EndMessage, WelcomeMessage } from '../messages.js';
import type { NetSnapshot } from '../snapshot.js';
import { VIEW_TTL, ghostExpired, ghostFromView } from './common.js';

export interface ViewInit {
  me: PlayerId;
  team: TeamId;
  mode: MatchMode;
  players: PlayerInfo[];
  landCount: number;
}

export function viewInitFromWelcome(w: WelcomeMessage, landCount: number): ViewInit {
  return { me: w.you.playerId, team: w.you.team, mode: w.settings.mode, players: w.players.map((p) => ({ ...p })), landCount };
}

function teammates(init: Pick<ViewInit, 'me' | 'team' | 'mode' | 'players'>): PlayerId[] {
  if (init.mode !== 'teams') return [];
  return init.players.filter((p) => p.team === init.team && p.id !== init.me).map((p) => p.id);
}

export function createPlayerView(init: ViewInit): PlayerView {
  const N = init.landCount;
  return {
    tick: 0,
    me: init.me,
    team: init.team,
    mode: init.mode,
    players: init.players,
    credits: 0,
    incomePerMin: 0,
    areaShare: 0,
    shares: [],
    owner: new Uint8Array(N),
    dens: new Uint8Array(N),
    rad: new Uint8Array(N),
    lastSeenTick: new Int32Array(N).fill(-1),
    live: new Uint8Array(N),
    entities: new Map(),
    ghosts: new Map(),
    tracks: new Map(),
    hints: [],
    detonations: [],
    intercepts: [],
    phases: [],
    allies: teammates(init),
    allyRequests: [],
    ceasefires: new Map(),
    ceasefireRequests: [],
    focus: null,
    released: false,
    finalPhase: false,
    eliminated: false,
    capitalsExposed: false,
    criticalCapitals: new Map(),
    front: { status: 'none', ratio: 0, rate: 0 },
    beaconLeader: 0,
    result: null,
  };
}

/** Synthetic ghost id for a back-plotted launch origin (never collides with entity ids). */
export const originContactId = (trackId: EntityId): EntityId => -trackId;

const CONTACT_MATCH_R2 = 1;

function resetForFull(v: PlayerView): void {
  v.live.fill(0);
  v.lastSeenTick.fill(-1);
  v.entities.clear();
  v.ghosts.clear();
  v.tracks.clear();
  v.hints = [];
  v.intercepts = [];
  v.phases = [];
  v.allyRequests = [];
  v.ceasefires.clear();
  v.ceasefireRequests = [];
  v.allies = teammates(v);
  v.released = false;
  v.finalPhase = false;
  v.capitalsExposed = false;
  v.criticalCapitals.clear();
  v.beaconLeader = 0;
}

function addOriginContact(v: PlayerView, t: MissileTrack): void {
  if (t.owner === v.me || v.allies.includes(t.owner)) return;
  const kind = t.kind === 'icbm' ? 'silo' : 'sub';
  const near = (x: number, y: number) => (x - t.ox) ** 2 + (y - t.oy) ** 2 <= CONTACT_MATCH_R2;
  for (const e of v.entities.values()) if (e.kind === kind && e.owner === t.owner && near(e.x, e.y)) return;
  for (const g of v.ghosts.values()) if (g.kind === kind && g.owner === t.owner && near(g.x, g.y)) return;
  const g: Ghost = { id: originContactId(t.id), kind, owner: t.owner, x: t.ox, y: t.oy, tick: t.launchTick };
  v.ghosts.set(g.id, g);
}

function setAlly(v: PlayerView, a: PlayerId, b: PlayerId, on: boolean): void {
  if (v.mode === 'teams' || (on && v.eliminated)) return;
  // pairs only: any two nations allying void every open alliance offer involving either of them
  // (the server drops them too) — also when the pair is not ours: an offer to us from a nation
  // that just allied someone else cannot be accepted any more
  if (on) v.allyRequests = v.allyRequests.filter((r) => r.from !== a && r.to !== a && r.from !== b && r.to !== b);
  const other = a === v.me ? b : b === v.me ? a : 0;
  if (!other) return;
  const has = v.allies.includes(other);
  if (on && !has) v.allies = [...v.allies, other].sort((x, y) => x - y);
  if (!on && has) v.allies = v.allies.filter((x) => x !== other);
  if (on) {
    // the alliance covers what a ceasefire between us did
    v.ceasefires.delete(other);
    v.ceasefireRequests = v.ceasefireRequests.filter((r) => r.from !== other && r.to !== other);
  }
}

/** The other side of a pair that includes us (0: not ours). */
const ours = (v: PlayerView, a: PlayerId, b: PlayerId): PlayerId => (a === v.me ? b : b === v.me ? a : 0);

/** Diplomacy phase events that change our ceasefires and open offers. */
function applyDiplomacy(v: PlayerView, kind: string, a: PlayerId, b: PlayerId, tick: number): void {
  const D = BALANCE.diplomacy;
  const other = ours(v, a, b);
  if (!other) return;
  const between = (r: { from: PlayerId; to: PlayerId }) => r.from === other || r.to === other;
  switch (kind) {
    case 'ceasefireOffer':
      if (!v.ceasefireRequests.some((r) => r.from === a && r.to === b)) v.ceasefireRequests.push({ from: a, to: b, expiresTick: tick + D.ceasefireRequestTicks });
      break;
    case 'ceasefireDeclined':
      v.ceasefireRequests = v.ceasefireRequests.filter((r) => !(r.from === a && r.to === b));
      break;
    case 'ceasefire':
      if (v.eliminated) break;
      v.ceasefires.set(other, tick + D.ceasefireTicks);
      v.ceasefireRequests = v.ceasefireRequests.filter((r) => !between(r));
      break;
    case 'ceasefireBroken':
      v.ceasefires.delete(other);
      break;
  }
}

function expire(v: PlayerView): void {
  const t = v.tick;
  for (const [id, g] of v.ghosts) {
    if (id < 0 ? t - g.tick > VIEW_TTL.originContact : ghostExpired(g, t)) v.ghosts.delete(id);
  }
  for (const [id, tr] of v.tracks) if (t > tr.launchTick + tr.flightTicks + VIEW_TTL.trackGrace) v.tracks.delete(id);
  if (v.hints.length && t - v.hints[0].tick > VIEW_TTL.hint) v.hints = v.hints.filter((h) => t - h.tick <= VIEW_TTL.hint);
  if (v.detonations.length && t - v.detonations[0].tick > VIEW_TTL.detonation) v.detonations = v.detonations.filter((d) => t - d.tick <= VIEW_TTL.detonation);
  if (v.intercepts.length) v.intercepts = v.intercepts.filter((i) => t - i.resolveTick <= VIEW_TTL.intercept);
  if (v.phases.length && t - v.phases[0].tick > VIEW_TTL.phase) v.phases = v.phases.filter((p) => t - p.tick <= VIEW_TTL.phase);
  if (v.allyRequests.length) v.allyRequests = v.allyRequests.filter((r) => r.expiresTick > t);
  if (v.ceasefireRequests.length) v.ceasefireRequests = v.ceasefireRequests.filter((r) => r.expiresTick > t);
  for (const [p, until] of v.ceasefires) if (until <= t) v.ceasefires.delete(p);
  // the server sends `capitalRecovered` when a window runs out; this only guards a stale entry
  for (const [p, until] of v.criticalCapitals) if (t > until + VIEW_TTL.trackGrace) v.criticalCapitals.delete(p);
}

/** Apply one decoded snapshot (delta or full) to the view. Mutates and returns `view`. */
export function applySnapshot(view: PlayerView, s: NetSnapshot): PlayerView {
  const v = view;
  const tick = s.hdr.tick;
  if (s.full) resetForFull(v);

  // header
  v.tick = tick;
  v.credits = s.hdr.credits;
  v.incomePerMin = s.hdr.incomePerMin;
  v.areaShare = s.hdr.areaShare;
  v.focus = s.hdr.focus < 0 ? null : s.hdr.focus;
  if (s.hdr.flags & SNAP_FLAG_RELEASED) v.released = true;
  if (s.hdr.flags & SNAP_FLAG_FINAL) v.finalPhase = true;
  if (s.hdr.flags & SNAP_FLAG_CAPITALS_EXPOSED) v.capitalsExposed = true;
  v.eliminated = (s.hdr.flags & SNAP_FLAG_ELIMINATED) !== 0;
  // a player who is out has no side (the sim dissolves their alliance without an event)
  if (v.eliminated) v.allies = [];
  v.front = s.hdr.front;

  // hexes
  if (s.full && s.lastSeen) v.lastSeenTick.set(s.lastSeen);
  for (const h of s.live) v.live[h] ^= 1;
  const live = v.live;
  const seen = v.lastSeenTick;
  for (let h = 0; h < live.length; h++) if (live[h]) seen[h] = tick;
  for (const o of s.owners) v.owner[o.hex] = o.owner;
  for (const p of s.popRad) {
    v.dens[p.hex] = p.dens;
    v.rad[p.hex] = p.rad;
  }

  // entities
  for (const r of s.removes) {
    const e = v.entities.get(r.id);
    if (e) {
      v.entities.delete(r.id);
      if (r.reason === 'lostContact') v.ghosts.set(r.id, ghostFromView(e, tick - 1));
      else v.ghosts.delete(r.id);
    } else {
      v.ghosts.delete(r.id);
      v.tracks.delete(r.id);
    }
  }
  for (const e of s.upserts) {
    v.entities.set(e.id, e);
    v.ghosts.delete(e.id);
  }
  for (const g of s.ghosts) if (!v.entities.has(g.id)) v.ghosts.set(g.id, g);

  // missiles & events
  for (const t of s.tracks) {
    const isNew = !v.tracks.has(t.id);
    v.tracks.set(t.id, t);
    if (isNew) addOriginContact(v, t);
  }
  if (s.hints.length) v.hints.push(...s.hints);
  if (s.detonations.length) v.detonations.push(...s.detonations);
  for (const i of s.intercepts) {
    if (i.hit !== undefined) {
      const k = v.intercepts.findIndex((x) => x.hit === undefined && x.shooterId === i.shooterId && x.targetId === i.targetId && x.fireTick === i.fireTick);
      if (k >= 0) {
        v.intercepts[k] = i;
        continue;
      }
    }
    v.intercepts.push(i);
  }
  if (s.shares.length) v.shares = s.shares;
  for (const ph of s.phase) {
    v.phases.push(ph);
    switch (ph.kind) {
      case 'release':
        v.released = true;
        break;
      case 'final':
        v.finalPhase = true;
        break;
      case 'capitalsExposed':
        v.capitalsExposed = true;
        break;
      case 'beacon':
        v.beaconLeader = ph.a;
        break;
      case 'ally':
        setAlly(v, ph.a, ph.b, true);
        break;
      case 'allyBroken':
        setAlly(v, ph.a, ph.b, false);
        break;
      case 'capitalCritical':
        v.criticalCapitals.set(ph.a, ph.tick + ph.b * 10);
        break;
      case 'capitalRecovered':
        v.criticalCapitals.delete(ph.a);
        break;
      case 'allyDeclined':
        v.allyRequests = v.allyRequests.filter((r) => !(r.from === ph.a && r.to === ph.b));
        break;
      case 'ceasefireOffer':
      case 'ceasefireDeclined':
      case 'ceasefire':
      case 'ceasefireBroken':
        applyDiplomacy(v, ph.kind, ph.a, ph.b, ph.tick);
        break;
      case 'eliminated':
        v.criticalCapitals.delete(ph.a);
        v.ceasefires.delete(ph.a);
        v.ceasefireRequests = v.ceasefireRequests.filter((r) => r.from !== ph.a && r.to !== ph.a);
        v.allyRequests = v.allyRequests.filter((r) => r.from !== ph.a && r.to !== ph.a);
        if (ph.a === v.me) {
          v.ceasefires.clear();
          v.ceasefireRequests = [];
          v.allyRequests = [];
        }
        // an ally knocked out no longer binds us (the sim clears the pair without an
        // `allyBroken`); our own knock-out leaves us with no side at all
        if (ph.a === v.me) v.allies = [];
        else if (v.allies.includes(ph.a)) v.allies = v.allies.filter((x) => x !== ph.a);
        break;
    }
  }
  // (a prompt and an alliance that voids it can arrive in the same snapshot: the alliance wins)
  const allied = new Set<PlayerId>();
  for (const ph of s.phase) if (ph.kind === 'ally') allied.add(ph.a).add(ph.b);
  for (const r of s.allyPrompts) {
    if (allied.has(r.from) || allied.has(r.to)) continue;
    if (!v.allyRequests.some((x) => x.from === r.from && x.to === r.to && x.expiresTick === r.expiresTick)) v.allyRequests.push(r);
  }
  expire(v);
  return v;
}

/** Record the match result from the `end` message. */
export function applyMatchEnd(view: PlayerView, end: EndMessage | MatchResult): PlayerView {
  view.result = { reason: end.reason, winners: end.winners, standings: end.standings, tick: 'tick' in end ? end.tick : view.tick };
  return view;
}
